import { PropsWithChildren, FC, PureComponent, ReactNode } from 'react'
import bluebird from 'bluebird'
import pluralize from 'pluralize'
import clamp from 'lodash/clamp'
import size from 'lodash/size'
import sum from 'lodash/sum'
import uniqueId from 'lodash/uniqueId'
import { gql, useMutation } from '@apollo/client'
import { notification, Progress, Alert } from 'antd'

import Link from '~/core/Link'
import getErrorMessage from '~/lib/getErrorMessage'
import logger from '~/lib/logger'
import {
    createZipDownloadVariables,
    createZipDownload as createZipDownloadData,
} from '~/core/startDownload/__generated__/createZipDownload'
import {
    createEstimateItemZipDownload,
    createEstimateItemZipDownloadVariables,
} from '~/core/startDownload/__generated__/createEstimateItemZipDownload'

import createZipDownload from './createZipDownload'

const REFRESH_TIME = 1000
const MAX_TRIES = 5
const ONE_HUNDRED = 100
const GENERIC_ERROR =
    'Oops, something went wrong. Try again or get in touch with us.'

function getPercent(a: number, b: number) {
    return clamp((a / b) * ONE_HUNDRED, 0, ONE_HUNDRED)
}

interface State {
    url: string | null
    tries: number
    error: string | null
}

export interface BaseDownloadMessageProps {
    id?: string
    fetchZip: () => Promise<string | null | undefined>
    openCompleteNotification?: boolean
    notificationKey?: string
    total?: number
    waitingContent?: () => ReactNode
    autoDownload?: boolean
}

export class BaseDownloadMessage extends PureComponent<
    BaseDownloadMessageProps,
    State
> {
    private _promise: bluebird<any> | null = null
    private _timer: NodeJS.Timeout | null = null
    state = { url: null, tries: 0, error: null }

    openCompleteNotification = (url: string, notificationKey?: string) => {
        notification.open({
            key: notificationKey,
            duration: null,
            description: (
                <>
                    {'Your download should begin automatically. If not, '}
                    <Link
                        name="download"
                        onClick={this.onClose}
                        placement="download-message"
                        href={url}
                        target="_blank"
                    >
                        Click here to download.
                    </Link>
                </>
            ),
            message: 'Your download is ready',
        })
    }
    reload = () => {
        const tries = this.state.tries + 1

        if (this._promise) {
            return
        }

        // Wrap in bluebird to retain cancellation sematics
        this._promise = bluebird.resolve(
            this.props
                .fetchZip()
                .then(url => {
                    if (url) {
                        this.setState({
                            url,
                            error: null,
                        })

                        const { autoDownload = true } = this.props

                        // don't auto-download in Cypress because it hangs on file downloads
                        // https://github.com/cypress-io/cypress/issues/949
                        if (autoDownload && !window.Cypress) {
                            window.open(url, '_self')
                        }

                        if (this.props.openCompleteNotification) {
                            this.openCompleteNotification(
                                url,
                                this.props.notificationKey,
                            )
                        }
                    } else if (tries > MAX_TRIES) {
                        this.setState({
                            error: GENERIC_ERROR,
                        })
                    } else {
                        const backOffMultiplier = Math.min(tries, 3)

                        this._timer = setTimeout(
                            this.reload,
                            REFRESH_TIME * backOffMultiplier,
                        )
                        this.setState({ tries: tries + 1 })
                    }

                    return null
                })
                .catch(error => {
                    logger.exception(error)
                    this.setState({
                        error: getErrorMessage(error),
                        url: null,
                    })
                })
                .finally(() => {
                    this._promise = null
                }),
        )
    }

    componentDidMount() {
        this.reload()
    }

    componentWillUnmount() {
        if (this._timer) {
            clearTimeout(this._timer)
            this._timer = null
        }
        if (this._promise && typeof this._promise.cancel === 'function') {
            this._promise.cancel()
        }
    }

    onClose = () => {
        notification.close(this.props.id || '')
    }

    render() {
        const { url, error, tries } = this.state

        if (error) {
            return <Alert message={error} type="error" />
        }

        const { total, waitingContent } = this.props
        const percent =
            url || typeof total === 'undefined'
                ? ONE_HUNDRED
                : getPercent(tries, total)
        let fileText = 'Zipping files for download...'

        if (total) {
            fileText = `Zipping ${total} ${pluralize(
                'file',
                total,
            )} for download...`
        }
        if (percent < 1) {
            fileText = 'Preparing files for download'
        }

        if (!url) {
            return waitingContent ? (
                waitingContent()
            ) : (
                <>
                    {fileText}
                    <Progress percent={percent} showInfo />
                </>
            )
        }

        return (
            <>
                {'Your download should begin automatically. If not, '}
                <Link
                    name="download"
                    onClick={this.onClose}
                    placement="download-message"
                    href={url}
                    target="_blank"
                >
                    Click here to download.
                </Link>
            </>
        )
    }
}

interface DownloadMessageProps
    extends Omit<BaseDownloadMessageProps, 'fetchZip'>,
        Omit<createZipDownloadVariables, 'folderId'> {
    quoteId: string
}

const DownloadMessage: FC<PropsWithChildren<DownloadMessageProps>> = ({
    revisionIds,
    attachmentIds,
    quoteId,
    name,
    quoteModelRevisionIds,
    quoteAttachmentRevisionIds,
    estimateItemIds,
    notificationKey,
    ...rest
}) => {
    const [mutation] = useMutation<
        createZipDownloadData,
        createZipDownloadVariables
    >(createZipDownload)

    return (
        <BaseDownloadMessage
            {...rest}
            notificationKey={notificationKey}
            openCompleteNotification
            fetchZip={async () => {
                const response = await mutation({
                    variables: {
                        revisionIds,
                        attachmentIds,
                        folderId: quoteId, //Change this to quoteId once graphql supports
                        name,
                        quoteModelRevisionIds,
                        quoteAttachmentRevisionIds,
                        estimateItemIds,
                    },
                })

                return response.data?.createZipDownload.url
            }}
        />
    )
}

interface EstimatesDownloadMessageProps
    extends Omit<BaseDownloadMessageProps, 'fetchZip'>,
        createEstimateItemZipDownloadVariables {}

const EstimatesDownloadMessage: FC<
    PropsWithChildren<EstimatesDownloadMessageProps>
> = props => {
    const [mutation] = useMutation<
        createEstimateItemZipDownload,
        createEstimateItemZipDownloadVariables
    >(gql`
        mutation createEstimateItemZipDownload(
            $name: String!
            $estimateItemIds: [UUID!]!
            $mode: EstimateItemZipMode!
        ) {
            createEstimateItemZipDownload(
                name: $name
                estimateItemIds: $estimateItemIds
                mode: $mode
            ) {
                id
                error
                url
                name
                jobStartDate
                jobCompleteDate
            }
        }
    `)

    const { name, estimateItemIds, mode } = props

    return (
        <BaseDownloadMessage
            {...props}
            openCompleteNotification
            fetchZip={async () => {
                const response = await mutation({
                    variables: {
                        name,
                        estimateItemIds,
                        mode,
                    },
                })

                return response.data?.createEstimateItemZipDownload.url
            }}
        />
    )
}

export default function startDownload({
    revisionIds = [],
    attachmentIds = [],
    quoteModelRevisionIds = [],
    quoteAttachmentRevisionIds = [],
    estimateItemIds = [],
    quoteId,
    name,
}: Pick<
    DownloadMessageProps,
    | 'revisionIds'
    | 'attachmentIds'
    | 'quoteModelRevisionIds'
    | 'quoteAttachmentRevisionIds'
    | 'estimateItemIds'
    | 'quoteId'
    | 'name'
>) {
    const downloadNotificationKey = uniqueId('download-message')
    const total = sum([
        size(revisionIds),
        size(attachmentIds),
        size(quoteAttachmentRevisionIds),
        size(quoteModelRevisionIds),
        size(estimateItemIds),
    ])

    if (total < 1) {
        return
    }

    notification.open({
        key: downloadNotificationKey,
        duration: null,
        description: (
            <DownloadMessage
                id={downloadNotificationKey}
                name={name}
                total={total}
                quoteId={quoteId}
                quoteModelRevisionIds={quoteModelRevisionIds}
                quoteAttachmentRevisionIds={quoteAttachmentRevisionIds}
                revisionIds={revisionIds}
                attachmentIds={attachmentIds}
                estimateItemIds={estimateItemIds}
                notificationKey={downloadNotificationKey}
            />
        ),
        message: 'Download files',
    })
}

export function startEstimateZipDownload({
    estimateItemIds = [],
    mode,
    name,
}: Pick<EstimatesDownloadMessageProps, 'estimateItemIds' | 'mode' | 'name'>) {
    const estimateDownloadNotificationKey = uniqueId(
        'download-estimates-message',
    )
    const total = size(estimateItemIds)

    if (total < 1) {
        return
    }

    notification.open({
        key: estimateDownloadNotificationKey,
        duration: null,
        description: (
            <EstimatesDownloadMessage
                id={estimateDownloadNotificationKey}
                name={name}
                mode={mode}
                total={total}
                estimateItemIds={estimateItemIds}
                notificationKey={estimateDownloadNotificationKey}
            />
        ),
        message: 'Download files',
    })
}
